import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAllIngredients, useAllS4DProducts, useProductSizes } from '../../../apis/products-api';
import { useTranslation } from 'react-i18next';
import { getConfiguration } from '../../../shared/utils/configuration';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { S4DIssueList } from './S4DIssueList';
import { getS4DIssues, S4DPage } from '../utils/s4dIssueMessages';
import { getS4DProductHref, getS4DToppingHref } from '../utils/s4dAdminLinks';
import { S4DOptionDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { formatDecimal } from '../../../shared/utils/formatters';
import { ProductSizeModel } from '../types';
import {
  expectsDoughOptions,
  findOption,
  getDoughsInUse,
  getPriceComparableDoughIds,
  getS4DProductsForProduct,
  pricesDiffer,
  S4DProductComparison,
  S4DProductStatus,
} from '../utils/s4dProductComparison';

type Props = {
  productId: number;
  productCategoryId: number;
  productSizePrices: ProductSizeModel[];
  recipeIngredientIds: number[];
  comparison: S4DProductComparison | undefined;
};

type PriceCellsProps = {
  option: S4DOptionDto | undefined;
  ourPrice: number | undefined;
  isComparable: boolean;
};

const S4DPriceCells = (props: PriceCellsProps) => {
  const { option, ourPrice, isComparable } = props;
  const { t } = useTranslation();

  if (!option) {
    return (
      <>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
      </>
    );
  }

  const otherPrices = (option.priceLevels || []).filter((level) => !level.isDefault);

  const renderComparedPrice = (price: number | undefined | null) => {
    if (!isComparable) {
      return (
        <Tooltip title={t('This dough is priced separately in S4D, so the price is not compared')}>
          <Typography variant="body2" color="text.secondary">
            {price === undefined || price === null ? t('missing') : formatDecimal(price, ' €')}
          </Typography>
        </Tooltip>
      );
    }

    // S4D should always have both a delivery and a pickup price for an option.
    if (price === undefined || price === null) {
      return (
        <Typography variant="body2" color="error" display="flex" alignItems="center" gap={0.5}>
          {t('missing')}
          <Tooltip title={t('The price is missing from S4D')}>
            <Warning color="error" fontSize="small" />
          </Tooltip>
        </Typography>
      );
    }

    const differs = ourPrice !== undefined && pricesDiffer(ourPrice, price);

    return (
      <Typography variant="body2" color={differs ? 'error' : 'inherit'} display="flex" alignItems="center" gap={0.5}>
        {formatDecimal(price, ' €')}
        {differs && (
          <Tooltip
            title={t('Price differs from ours: {{ourPrice}}', { ourPrice: formatDecimal(ourPrice as number, ' €') })}
          >
            <Warning color="error" fontSize="small" />
          </Tooltip>
        )}
      </Typography>
    );
  };

  return (
    <>
      <TableCell>{renderComparedPrice(option.price)}</TableCell>
      <TableCell>{renderComparedPrice(option.pricePickupDefault)}</TableCell>
      <TableCell>
        {otherPrices.map((level) => (
          <Typography variant="body2" key={level.pricingLevelId}>
            {level.name}: {formatDecimal(level.price, ' €')}
          </Typography>
        ))}
      </TableCell>
    </>
  );
};

const ProductLinks = (props: Props) => {
  const { productId, productCategoryId, productSizePrices, recipeIngredientIds, comparison } = props;
  const { t } = useTranslation();
  const s4dAdminBaseUrl = getConfiguration<string>('s4d.admin', '');

  const {
    data: s4dProducts,
    isInitialLoading: isInitialLoadingS4DProducts,
    isFetching: isFetchingS4DProducts,
  } = useAllS4DProducts();

  const {
    data: ingredients,
    isInitialLoading: isInitialLoadingIngredients,
    isFetching: isFetchingIngredients,
  } = useAllIngredients();

  const { data: productSizes } = useProductSizes(productId);

  if (isInitialLoadingS4DProducts || isFetchingS4DProducts || isInitialLoadingIngredients || isFetchingIngredients) {
    return null;
  }

  if (!productSizes) return null;

  const doughs = getDoughsInUse(ingredients || []);

  // A product can have several S4D products: a Monster and its Large version are separate there,
  // each carrying the options of its own size.
  const matchingS4DProducts = getS4DProductsForProduct(s4dProducts || [], productId);
  const options = matchingS4DProducts.flatMap((s4dProduct) => s4dProduct.options);

  const defaultSize = [...productSizes].shift();

  const nonDoughPriceOptions = options.filter((o) => o.doughId === 0);

  const hasPrice = options.length > 0;

  const comparableDoughIds = getPriceComparableDoughIds(recipeIngredientIds, options);

  const getOurPrice = (productSizeId: number): number | undefined => {
    const sizePrice = productSizePrices.find((size) => size.sizeId === productSizeId);
    const price = sizePrice?.minimumPrice || sizePrice?.calculatedPrice;
    // 0 until the price is calculated, and some sizes never are.
    return price && price > 0 ? price : undefined;
  };

  const renderOurPrice = (productSizeId: number) => {
    const ourPrice = getOurPrice(productSizeId);
    return <TableCell>{ourPrice === undefined ? '' : formatDecimal(ourPrice, ' €')}</TableCell>;
  };

  const getIngredientName = (ingredientId: number) =>
    ingredients?.find((ingredient) => ingredient.ingredientId === ingredientId)?.name;

  const issues = comparison ? getS4DIssues({ comparison, t, getIngredientName }) : [];

  return (
    <>
      {comparison && (
        <Box display="flex" alignItems="flex-start" gap={1} pb={2}>
          {comparison.status === S4DProductStatus.NotCompared ? (
            <Typography variant="body2">{t('Product is archived, S4D data is not compared')}</Typography>
          ) : issues.length === 0 ? (
            <>
              <CheckCircle color="success" fontSize="small" />
              <Typography variant="body2">{t('S4D data matches')}</Typography>
            </>
          ) : (
            <S4DIssueList issues={issues} toppingHref={(topping) => getS4DToppingHref(s4dAdminBaseUrl, topping)} />
          )}
        </Box>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>{t('global.status')}</TableCell>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <TableCell>WebCode</TableCell>
              <TableCell>{t('global.id')}</TableCell>
              <TableCell>{t('Our price')}</TableCell>
              <TableCell>{t('S4D delivery price')}</TableCell>
              <TableCell>{t('S4D pickup price')}</TableCell>
              <TableCell>{t('Other S4D prices')}</TableCell>
              <TableCell>{t('global.link')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matchingS4DProducts.map((s4dProduct) => (
              <TableRow key={s4dProduct.s4dId}>
                <TableCell>{s4dProduct.name}</TableCell>
                <TableCell>
                  {s4dProduct.webCode?.trim() === String(productId) ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Error color="error" />
                  )}
                </TableCell>
                <TableCell>{productId}</TableCell>
                <TableCell>{s4dProduct.s4dId}</TableCell>
                <TableCell></TableCell>
                <S4DPriceCells option={undefined} ourPrice={undefined} isComparable />
                <TableCell>
                  <Box display="flex" flexDirection="column">
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={getS4DProductHref(s4dAdminBaseUrl, s4dProduct.s4dId, S4DPage.Product)}
                    >
                      {t('S4D product')}
                    </Link>
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={getS4DProductHref(s4dAdminBaseUrl, s4dProduct.s4dId, S4DPage.Prices)}
                    >
                      {t('S4D prices')}
                    </Link>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {matchingS4DProducts.length === 0 && (
              <TableRow>
                <TableCell>{t('Product or WebCode missing from S4D')}</TableCell>
                <TableCell>
                  <Error color="error" />
                </TableCell>
                <TableCell>{productId}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <S4DPriceCells option={undefined} ourPrice={undefined} isComparable />
                <TableCell>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <Link href={`${s4dAdminBaseUrl}/Content/Secure/Menu/Products/Overview.aspx`}>S4D Admin</Link>
                </TableCell>
              </TableRow>
            )}
            {productSizes?.map((size) => {
              if (!expectsDoughOptions(size.productSizeId, productCategoryId, options)) return null;

              return doughs
                .filter((dough) => dough.dosings?.map((d) => d.productSizeId).includes(size.productSizeId))
                .map((dough) => {
                  const webCode = `${size?.productSizeId}_${dough?.ingredientId}`;
                  const option = findOption(options, size.productSizeId, dough.ingredientId || 0);
                  return (
                    <TableRow key={`${size.name}-${dough.name}`}>
                      <TableCell>{`${size.name} - ${dough.name}`}</TableCell>
                      <TableCell>{option ? <CheckCircle color="success" /> : <Error color="error" />}</TableCell>
                      <TableCell>{webCode}</TableCell>
                      <TableCell>{option ? option.id : ''}</TableCell>
                      {renderOurPrice(size.productSizeId)}
                      <S4DPriceCells
                        option={option}
                        ourPrice={getOurPrice(size.productSizeId)}
                        isComparable={comparableDoughIds.includes(dough.ingredientId || -1)}
                      />
                      <TableCell></TableCell>
                    </TableRow>
                  );
                });
            })}
            {nonDoughPriceOptions &&
              nonDoughPriceOptions.map((opt) => {
                return (
                  <TableRow key={opt.id}>
                    <TableCell>{t('global.price')}</TableCell>
                    <TableCell>
                      <CheckCircle color="success" />
                    </TableCell>
                    <TableCell>{opt.webCode}</TableCell>
                    <TableCell>{opt.id}</TableCell>
                    {renderOurPrice(opt.productSizeId)}
                    <S4DPriceCells option={opt} ourPrice={getOurPrice(opt.productSizeId)} isComparable />
                    <TableCell></TableCell>
                  </TableRow>
                );
              })}
            {!hasPrice && (
              <TableRow>
                <TableCell>
                  {t('global.price')} {t('missing')}
                </TableCell>
                <TableCell>
                  <Error color="error" />
                </TableCell>
                <TableCell>{`${defaultSize?.productSizeId}_0`}</TableCell>
                <TableCell></TableCell>
                {defaultSize ? renderOurPrice(defaultSize.productSizeId) : <TableCell></TableCell>}
                <S4DPriceCells option={undefined} ourPrice={undefined} isComparable />
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ProductLinks;
